/**
 * Quiz readiness / preflight — shared by the Game-Night Hub and the
 * Dashboard so both judge "is this quiz ready to run" the same way.
 * Pure functions over quiz content; no fetching.
 */

export interface PreflightQuestion {
  id?: string;
  orderIndex?: number;
  text?: string;
  type: 'TEXT' | 'SONG' | 'IMAGE';
  answer: string;
  audioPath: string | null;
  imagePath: string | null;
  // Multiple choice: 2 or more choices, and the index of the correct one.
  choices?: string[] | null;
  correctChoice?: number | null;
}

export interface PreflightRound {
  orderIndex?: number;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  questions: PreflightQuestion[];
}

/** A multiple choice question with no correct choice set. */
export interface MissingChoiceKey {
  questionId: string;
  roundNumber: number;
  questionNumber: number;
  text: string;
}

/**
 * Multiple choice questions with no correct choice. Phones mark every
 * answer to such a question wrong, so the host must fix them before the
 * night starts. Numbers are 1-based, as the host sees them.
 */
export interface ChoiceCheckRound {
  orderIndex?: number;
  questions: readonly Pick<PreflightQuestion, 'id' | 'orderIndex' | 'text' | 'choices' | 'correctChoice'>[];
}

export function missingChoiceKeys(rounds: readonly ChoiceCheckRound[]): MissingChoiceKey[] {
  const out: MissingChoiceKey[] = [];
  rounds.forEach((r, ri) => {
    r.questions.forEach((q, qi) => {
      const choices = q.choices ?? [];
      const correct = q.correctChoice;
      const valid = correct != null && correct >= 0 && correct < choices.length;
      if (choices.length >= 2 && !valid) {
        out.push({
          questionId: q.id ?? '',
          roundNumber: (r.orderIndex ?? ri) + 1,
          questionNumber: (q.orderIndex ?? qi) + 1,
          text: q.text ?? '',
        });
      }
    });
  });
  return out;
}

/** Short label for one missing key, for example "R2 Q5". */
export function missingChoiceLabel(m: MissingChoiceKey): string {
  return `R${m.roundNumber} Q${m.questionNumber}`;
}

export interface ContentCounts {
  totalQuestions: number;
  songs: number;
  songsReady: number;
  images: number;
  imagesReady: number;
  answered: number;
  clipsMissing: number;
  imagesMissing: number;
  answersMissing: number;
  // Multiple choice questions with no correct choice set.
  choiceKeysMissing: number;
}

export function quizContentCounts(rounds: PreflightRound[]): ContentCounts {
  const all = rounds.flatMap((r) => r.questions);
  const songs = all.filter((q) => q.type === 'SONG');
  const images = all.filter((q) => q.type === 'IMAGE');
  const songsReady = songs.filter((q) => q.audioPath).length;
  const imagesReady = images.filter((q) => q.imagePath).length;
  const answered = all.filter((q) => q.answer && q.answer.trim()).length;
  return {
    totalQuestions: all.length,
    songs: songs.length,
    songsReady,
    images: images.length,
    imagesReady,
    answered,
    clipsMissing: songs.length - songsReady,
    imagesMissing: images.length - imagesReady,
    answersMissing: all.length - answered,
    choiceKeysMissing: missingChoiceKeys(rounds).length,
  };
}

export type ReadinessStatus =
  | { kind: 'ready' }
  | { kind: 'empty' }
  | { kind: 'clips-missing'; count: number }
  | { kind: 'images-missing'; count: number }
  | { kind: 'answers-missing'; count: number }
  | { kind: 'choices-missing'; count: number };

// Single readiness verdict for a quiz card. Priority: empty → missing
// answers → ready. MP3 clips and visual images are OPTIONAL — a quiz with
// no uploaded audio/images is still fully runnable (hosts can play audio
// off-platform and visual rounds are read aloud), so only missing *answers*
// (which the host needs to score) hold a quiz back from ready.
export function quizReadiness(rounds: PreflightRound[]): ReadinessStatus {
  const c = quizContentCounts(rounds);
  if (c.totalQuestions === 0) return { kind: 'empty' };
  if (c.answersMissing > 0) return { kind: 'answers-missing', count: c.answersMissing };
  // A multiple choice question with no correct choice marks every phone
  // answer wrong, so it holds the quiz back too.
  if (c.choiceKeysMissing > 0) return { kind: 'choices-missing', count: c.choiceKeysMissing };
  return { kind: 'ready' };
}

// ponytail: single-multiplier runtime estimate (~1.85 min/question incl.
// intros/summaries); refine from real event telemetry if it ever matters.
// Returns e.g. "~1h 55m" / "~40m" — callers phrase around it.
export function estimateRuntime(totalQuestions: number): string {
  const mins = Math.round((totalQuestions * 1.85) / 5) * 5;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `~${h}h ${String(m).padStart(2, '0')}m` : `~${m}m`;
}

// Short uppercase label + semantic tone for a readiness pill.
export function readinessLabel(s: ReadinessStatus): { text: string; tone: 'green' | 'amber' | 'gray' } {
  switch (s.kind) {
    case 'ready': return { text: 'READY', tone: 'green' };
    case 'empty': return { text: 'EMPTY', tone: 'gray' };
    case 'clips-missing': return { text: `${s.count} CLIP${s.count > 1 ? 'S' : ''} MISSING`, tone: 'amber' };
    case 'images-missing': return { text: `${s.count} IMAGE${s.count > 1 ? 'S' : ''} MISSING`, tone: 'amber' };
    case 'answers-missing': return { text: `${s.count} ANSWER${s.count > 1 ? 'S' : ''} MISSING`, tone: 'amber' };
    case 'choices-missing': return { text: `${s.count} CORRECT CHOICE${s.count > 1 ? 'S' : ''} MISSING`, tone: 'amber' };
  }
}
